/* eslint react-hooks/exhaustive-deps: "warn" */

import { useCallback, useState } from 'react';

import dynamic from 'next/dynamic';

import { FeatureGrid } from 'ui/atoms/FeatureGrid/FeatureGrid';
import { Tabs } from 'ui/atoms/Tabs/Tabs';
import { Section } from 'ui/molecules/Section/Section';

import styles from './GlobalCoverageSection.module.scss';

import { COVERED_COUNTRIES, REGION_TABS, STATS } from './GlobalCoverageSection.data';
import type { GlobalCoverageSectionProps } from './GlobalCoverageSection.types';

const WorldMapChart = dynamic(() => import('./WorldMapChart').then((m) => ({ default: m.WorldMapChart })), {
  ssr: false,
  loading: () => <div className={styles.mapPlaceholder} />,
});

export const GlobalCoverageSection = ({
  title,
  description,
  countries = COVERED_COUNTRIES,
  statsPosition = 'after',
}: GlobalCoverageSectionProps) => {
  const [activeRegion, setActiveRegion] = useState('All');
  const [selectedCountryId, setSelectedCountryId] = useState<string | null>(null);

  const handleTabClick = useCallback((region: string) => {
    setActiveRegion(region);
    setSelectedCountryId(null);
  }, []);

  const handleCountryClick = useCallback(
    (id: string | null) => {
      setSelectedCountryId(id);
      if (id && countries[id]) {
        setActiveRegion(countries[id].region);
      }
    },
    [countries],
  );

  return (
    <Section fullWidth title={title} description={description} className={styles.section}>
      {statsPosition === 'before' && (
        <FeatureGrid columns={4} hasCheckmark={false} items={STATS} className={styles.statsTop} />
      )}

      <div className={styles.worldMapBlock}>
        <Tabs
          size="lg"
          noBorder
          activeKey={activeRegion}
          tabs={REGION_TABS.map((r) => ({ key: r, label: r }))}
          onChange={handleTabClick}
          className={styles.tabs}
        />

        <WorldMapChart
          activeRegion={activeRegion}
          selectedCountryId={selectedCountryId}
          onCountryClick={handleCountryClick}
          countries={countries}
        />
      </div>

      {statsPosition !== 'before' && <FeatureGrid columns={4} hasCheckmark={false} items={STATS} />}
    </Section>
  );
};
